import { useState, useEffect, useRef, useCallback } from 'preact/hooks'
import { Monitor, Tablet, Smartphone, FileText, Heart, Loader2, AlertCircle, RefreshCw } from 'lucide-preact'
import { RocketLaunch } from './RocketLaunch'
import { injectSandboxStubs, replaceYouTubeEmbeds, replaceDownloadLinks, replaceCalendlyEmbeds } from '@/utils/htmlPreviewStubs'

type Viewport = 'desktop' | 'tablet' | 'mobile'
type Phase = 'loading' | 'blasting-off' | 'ready'

const viewportWidths: Record<Viewport, number> = {
    desktop: 1280,
    tablet: 768,
    mobile: 375,
}

interface HtmlPreviewProps {
    html: string | null
    thankYouHtml?: string | null
    loading?: boolean
    thankYouGenerating?: boolean
    generationError?: string | null
    thankYouGenerationError?: string | null
    onRetry?: () => void
    canGenerateThankYou?: boolean
    onGenerateThankYou?: () => void
}

const injectForceState = (rawHtml: string, state: string): string => {
    const script = `<script>window.__FORCE_STATE='${state}';</script>`
    return rawHtml.includes('</head>')
        ? rawHtml.replace('</head>', `${script}</head>`)
        : `${script}${rawHtml}`
}

export function HtmlPreview({ html, thankYouHtml, loading, thankYouGenerating, generationError, thankYouGenerationError, onRetry, canGenerateThankYou, onGenerateThankYou }: HtmlPreviewProps) {
    const [viewport, setViewport] = useState<Viewport>('desktop')
    const [phase, setPhase] = useState<Phase>(loading ? 'loading' : 'ready')
    const [activePage, setActivePage] = useState<'landing' | 'thankYou'>('landing')
    const [forceState, setForceState] = useState<'pre-event' | 'post-event'>('pre-event')
    // Buffer HTML so we don't reveal it until blast-off completes
    const [displayHtml, setDisplayHtml] = useState<string | null>(loading ? null : html)
    const phaseRef = useRef<Phase>(loading ? 'loading' : 'ready')
    const pendingHtmlRef = useRef<string | null>(null)
    const blastOffCompleteRef = useRef(false)

    useEffect(() => {
        // Generation failed at any point in the lifecycle — jump straight to the
        // terminal 'ready' phase so the error UI below renders instead of the
        // rocket animation looping indefinitely. Without this, a first-gen
        // failure leaves the blast-off animation playing with no html to reveal.
        if (generationError) {
            if (phaseRef.current !== 'ready') {
                phaseRef.current = 'ready'
                setPhase('ready')
            }
            return
        }

        if (loading) {
            phaseRef.current = 'loading'
            blastOffCompleteRef.current = false
            setPhase('loading')
            setDisplayHtml(null)
            setActivePage('landing')
        } else if (phaseRef.current === 'loading') {
            // Loading finished — start blast-off. HTML may arrive during animation.
            if (html) pendingHtmlRef.current = html
            phaseRef.current = 'blasting-off'
            setPhase('blasting-off')
        } else if (phaseRef.current === 'blasting-off') {
            // Mid blast-off — buffer html as it arrives
            if (html) pendingHtmlRef.current = html
            // If animation already finished and html just arrived, transition now
            if (html && blastOffCompleteRef.current) {
                phaseRef.current = 'ready'
                setDisplayHtml(html)
                pendingHtmlRef.current = null
                setPhase('ready')
                blastOffCompleteRef.current = false
            }
        } else {
            // Ready state — show html directly
            phaseRef.current = 'ready'
            setPhase('ready')
            setDisplayHtml(html)
        }
    }, [loading, html, generationError])

    const handleBlastOffComplete = useCallback(() => {
        blastOffCompleteRef.current = true
        if (pendingHtmlRef.current) {
            phaseRef.current = 'ready'
            setDisplayHtml(pendingHtmlRef.current)
            pendingHtmlRef.current = null
            setPhase('ready')
        }
        // No html yet — stay on blast-off screen. Effect will catch it when html arrives.
    }, [])

    const thankYouDisplayHtml = thankYouHtml ? injectForceState(thankYouHtml, forceState) : null
    const rawActiveHtml = activePage === 'landing' ? displayHtml : thankYouDisplayHtml
    const activeHtml = rawActiveHtml ? injectSandboxStubs(replaceDownloadLinks(replaceCalendlyEmbeds(replaceYouTubeEmbeds(rawActiveHtml)))) : null

    const viewportButtons: Array<{ key: Viewport; icon: typeof Monitor; label: string }> = [
        { key: 'desktop', icon: Monitor, label: 'Desktop' },
        { key: 'tablet', icon: Tablet, label: 'Tablet' },
        { key: 'mobile', icon: Smartphone, label: 'Mobile' },
    ]

    const activeButtonClass = 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
    const inactiveButtonClass = 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'

    if (phase === 'loading') {
        return <RocketLaunch phase="idle" />
    }

    if (phase === 'blasting-off') {
        return <RocketLaunch phase="blasting-off" onComplete={handleBlastOffComplete} />
    }

    if (!displayHtml && generationError) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
                <div className="flex flex-col items-center gap-4 max-w-md text-center px-6">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                    <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Generation failed</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Something went wrong generating your landing page. Please try again.</p>
                    </div>
                    {onRetry && (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Retry
                        </button>
                    )}
                </div>
            </div>
        )
    }

    if (!displayHtml) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900 text-sm text-slate-500 dark:text-slate-400">
                No preview available
            </div>
        )
    }

    const showThankYouSpinner = activePage === 'thankYou' && thankYouGenerating && !thankYouHtml
    const showThankYouError = activePage === 'thankYou' && thankYouGenerationError && !thankYouHtml
    const showThankYouCta = activePage === 'thankYou' && !thankYouHtml && !thankYouGenerating && !thankYouGenerationError && canGenerateThankYou

    return (
        <div className="h-full flex flex-col">
            {/* Viewport toolbar */}
            <div className="flex items-center gap-1 px-4 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shrink-0">
                {viewportButtons.map(({ key, icon: Icon, label }) => (
                    <button
                        key={key}
                        type="button"
                        title={label}
                        onClick={() => setViewport(key)}
                        className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                            viewport === key ? activeButtonClass : inactiveButtonClass
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{label}</span>
                    </button>
                ))}

                {/* Page switcher — pushed to right */}
                <div className="ml-auto border-l border-slate-200 dark:border-slate-700 pl-2 flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setActivePage('landing')}
                        className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                            activePage === 'landing' ? activeButtonClass : inactiveButtonClass
                        }`}
                    >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Landing Page</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActivePage('thankYou')}
                        className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                            activePage === 'thankYou' ? activeButtonClass : inactiveButtonClass
                        }`}
                    >
                        <Heart className="w-3.5 h-3.5" />
                        <span>Thank You</span>
                    </button>
                </div>

                {/* State toggle — only visible when viewing Thank You page */}
                {activePage === 'thankYou' && (
                    <div className="border-l border-slate-200 dark:border-slate-700 pl-2 ml-2 flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setForceState('pre-event')}
                            className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                                forceState === 'pre-event' ? activeButtonClass : inactiveButtonClass
                            }`}
                        >
                            <span>Pre-event</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setForceState('post-event')}
                            className={`p-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${
                                forceState === 'post-event' ? activeButtonClass : inactiveButtonClass
                            }`}
                        >
                            <span>Post-event</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Iframe container — fills remaining space */}
            <div className="flex-1 flex justify-center bg-slate-100 dark:bg-slate-900 overflow-auto">
                {showThankYouCta ? (
                    <div className="flex flex-col items-center justify-center gap-4 max-w-md text-center px-6">
                        <Heart className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                        <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">No thank you page yet</p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Generate a thank you page to show after form submission.</p>
                        </div>
                        {onGenerateThankYou && (
                            <button
                                type="button"
                                onClick={onGenerateThankYou}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                            >
                                <Heart className="w-4 h-4" />
                                Generate Thank You Page
                            </button>
                        )}
                    </div>
                ) : showThankYouError ? (
                    <div className="flex flex-col items-center justify-center gap-4 max-w-md text-center px-6">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                        <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Thank you page generation failed</p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Something went wrong generating the thank you page. Please try again.</p>
                        </div>
                    </div>
                ) : showThankYouSpinner ? (
                    <div className="flex flex-col items-center justify-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                        <span>Generating thank you page...</span>
                    </div>
                ) : (
                    <iframe
                        srcdoc={activeHtml ?? undefined}
                        sandbox="allow-scripts"
                        title="Landing page preview"
                        style={{ width: `${viewportWidths[viewport]}px`, maxWidth: '100%', height: '100%' }}
                        className="bg-white shadow-sm"
                    />
                )}
            </div>
        </div>
    )
}
